import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config/api';

/**
 * Reusable TaxonomySelect component
 * Fetches taxonomy items from the public API and renders a multi-select or single-select
 */
export const TaxonomySelect = ({
  type,           // e.g., 'level_of_study', 'field_of_study', etc.
  value = [],     // Current selected values (array of slugs or labels)
  onChange,       // Callback: (selectedValues) => void
  multiple = true, // Allow multiple selection
  label,          // Label for the field
  placeholder = 'Select...',
  required = false,
  className = ''
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchOptions();
  }, [type]);

  const fetchOptions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/public/master-taxonomy?type=${type}`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data.items || []);
      }
    } catch (err) {
      console.error(`Failed to fetch taxonomy ${type}:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.includes(item.label);
      
      if (isSelected) {
        onChange(currentValues.filter(v => v !== item.label));
      } else {
        onChange([...currentValues, item.label]);
      }
    } else {
      onChange(item.label);
      setIsOpen(false);
    }
  };

  const handleRemove = (itemLabel) => {
    if (multiple) {
      onChange(value.filter(v => v !== itemLabel));
    } else {
      onChange('');
    }
  };

  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  if (loading) {
    return (
      <div className={className}>
        {label && <label className="block text-sm font-semibold text-[#111827] mb-2">{label}</label>}
        <div className="px-4 py-2 border-2 border-[#E5E7EB] rounded-lg text-sm text-[#6B7280]">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-semibold text-[#111827] mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      {/* Selected Tags (for multi-select) */}
      {multiple && selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedValues.map((val, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-1 bg-[#EFF6FF] text-[#1E40AF] text-xs font-medium rounded-full"
            >
              {val}
              <button
                type="button"
                onClick={() => handleRemove(val)}
                className="hover:text-red-600"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      
      {/* Dropdown Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-left flex items-center justify-between text-sm"
      >
        <span className={selectedValues.length === 0 ? 'text-[#9CA3AF]' : 'text-[#111827]'}>
          {!multiple && selectedValues.length > 0 ? selectedValues[0] : placeholder}
        </span>
        <svg className={`w-4 h-4 text-[#6B7280] transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white border-2 border-[#E5E7EB] rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[#6B7280]">
              No options available. Add items in Taxonomy Manager.
            </div>
          ) : (
            options.map((item) => {
              const isSelected = selectedValues.includes(item.label);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-[#F3F4F6] flex items-center justify-between ${
                    isSelected ? 'bg-[#EFF6FF] text-[#1E40AF]' : 'text-[#111827]'
                  }`}
                >
                  {item.label}
                  {isSelected && (
                    <svg className="w-4 h-4 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
      
      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default TaxonomySelect;
